import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { useAuthStore } from '../../../shared/stores/useAuthStore';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import {
  selectQuotaExhausted,
  selectQuotaLimited,
  useExportQuotaStore,
} from '../../../shared/stores/useExportQuotaStore';
import type { RunExportFlowOptions } from '../../../shared/api/grpc/pcfExportClient';

import { useExportState } from '../hooks/useExportState';
import { useExportColumns } from '../hooks/useExportColumns';
import { useExportAccessibilityWorkflow } from '../hooks/useExportAccessibilityWorkflow';
import { useExportRowHighlights } from '../hooks/useExportRowHighlights';
import { useUnsavedMappingSettingsGuard } from '../hooks/useUnsavedMappingSettingsGuard';
import { useExportResultFeedback } from '../hooks/useExportResultFeedback';
import { useExportRunLog } from '../hooks/useExportRunLog';
import { useExportTour, type ExportTourRefs } from '../hooks/useExportTour';
import { useExportWorkspaceCommands } from '../hooks/useExportWorkspaceCommands';
import { usePcfVisualCheck } from '../hooks/usePcfVisualCheck';
import { useExportLiveSummary } from '../hooks/useExportLiveSummary';
import { useExportColumnParameters } from '../hooks/useExportColumnParameters';
import type { PropertyItem } from '../../../shared/ui/propertySelection/PropertySelectionPanel';
import ExportWorkspaceHeader, { EXPORT_LIVE_SUMMARY_ID } from './ExportWorkspaceHeader';
import ExportSystemsWorkspace from './ExportSystemsWorkspace';
import type { PipingSystem } from '../types';
import { summarizePipingSystems } from '../lib/exportSystemStatus';
import { indexExportFailuresBySystemName } from '../lib/exportFailurePresentation';
import {
  formatRemainingExportElementsStatus,
  getQuotaExhaustedMessage,
} from '../lib/exportQuota';
import ExportWorkspaceOverlays from './ExportWorkspaceOverlays';

interface ExportPcfContentProps {
  onStatusClick?: (system: PipingSystem) => void;
  showGuideButton?: boolean;
  registerEmbeddedActions?:
    | ((actions: { openGuide: () => void; openTour?: () => void } | null) => void)
    | undefined;
  exportActionsDisabled?: boolean;
  exportActionsDisabledReason?: string;
  profile?: RunExportFlowOptions['profile'];
}

const EXPORT_BACKGROUND_ERROR_PATTERN =
  /runtime is unreachable|transport is unavailable/i;

export default function ExportPcfContent({
  onStatusClick,
  showGuideButton = true,
  registerEmbeddedActions,
  exportActionsDisabled = false,
  exportActionsDisabledReason,
  profile,
}: ExportPcfContentProps) {
  const theme = useTheme();
  const useDrawerPanel = useMediaQuery(theme.breakpoints.down('lg'));
  const useNarrowExportLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const showSuccess = useSnackbarStore((state) => state.showSuccess);
  const showError = useSnackbarStore((state) => state.showError);
  const showWarning = useSnackbarStore((state) => state.showWarning);
  const exportState = useExportState(profile);
  const {
    confirmExportWithUnsavedSettings,
    unsavedSettingsGuardDialog,
  } = useUnsavedMappingSettingsGuard();
  const columns = useExportColumns();
  const { rowHighlightColors, handleRowHighlightColorChange, handleRowHighlightReset } = useExportRowHighlights();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [columnPanelOpen, setColumnPanelOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [systemNameFilterValue, setSystemNameFilterValue] = useState('');
  const [accessibilityAnnouncement, setAccessibilityAnnouncement] = useState('PCF export workspace ready.');
  const headerRef = useRef<HTMLDivElement | null>(null);
  const refreshButtonRef = useRef<HTMLButtonElement | null>(null);
  const systemCountRef = useRef<HTMLElement | null>(null);
  const rowHighlightButtonRef = useRef<HTMLButtonElement | null>(null);
  const visualCheckButtonRef = useRef<HTMLButtonElement | null>(null);
  const selectionSummaryRef = useRef<HTMLDivElement | null>(null);
  const systemTableRef = useRef<HTMLDivElement | null>(null);
  const systemTableToolsRef = useRef<HTMLElement | null>(null);
  const systemTableRowsRef = useRef<HTMLElement | null>(null);
  const firstSystemRowRef = useRef<HTMLElement | null>(null);
  const activeColumnPanelRef = useRef<HTMLDivElement | null>(null);
  const activePropertySearchRef = useRef<HTMLInputElement | null>(null);
  const inlineColumnPanelRef = useRef<HTMLDivElement | null>(null);
  const drawerColumnPanelRef = useRef<HTMLDivElement | null>(null);
  const inlinePropertySearchRef = useRef<HTMLInputElement | null>(null);
  const drawerPropertySearchRef = useRef<HTMLInputElement | null>(null);
  const manageColumnsButtonRef = useRef<HTMLButtonElement | null>(null);
  const exportSettingsButtonRef = useRef<HTMLButtonElement | null>(null);
  const exportSettingsDialogRef = useRef<HTMLDivElement | null>(null);
  const settingsGroupingRef = useRef<HTMLElement | null>(null);
  const settingsOutputPathRef = useRef<HTMLElement | null>(null);
  const settingsValidationRef = useRef<HTMLElement | null>(null);
  const exportActionsRef = useRef<HTMLDivElement | null>(null);
  const outputPathRef = useRef<HTMLDivElement | null>(null);
  const exportButtonRef = useRef<HTMLButtonElement | null>(null);
  const pickAndExportButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusBeforeOverlayRef = useRef<HTMLElement | null>(null);

  const { exportResult, error, clearExportResult, clearError } = exportState;
  const exportLog = useExportRunLog(exportResult);
  const { recordResult: recordExportResult } = exportLog;

  const accessToken = useAuthStore((state) => state.accessToken);
  const quotaIsDemo = useExportQuotaStore((state) => state.isDemo);
  const quotaRemaining = useExportQuotaStore((state) => state.remainingExportElements);
  const quotaExhausted = useExportQuotaStore(selectQuotaExhausted);
  const quotaLimited = useExportQuotaStore(selectQuotaLimited);
  useExportResultFeedback({
    result: exportResult,
    quotaIsDemo,
    onAnnounce: setAccessibilityAnnouncement,
    onClear: clearExportResult,
  });

  useEffect(() => {
    const quotaStore = useExportQuotaStore.getState();
    quotaStore.activate();
    if (!accessToken) {
      return;
    }
    void quotaStore.loadCurrentLicense();
  }, [accessToken]);

  const quotaExhaustedMessage = getQuotaExhaustedMessage(quotaIsDemo);
  const exportActionsBlocked = exportActionsDisabled || quotaExhausted;
  const exportBlockedMessage = quotaExhausted
    ? quotaExhaustedMessage
    : exportActionsDisabledReason || 'Load and check a mapping profile for this Revit project before exporting PCF files.';

  const notifyExportBlocked = useCallback(() => {
    showError(exportBlockedMessage);
    setAccessibilityAnnouncement(exportBlockedMessage);
  }, [exportBlockedMessage, showError]);
  const rememberFocusBeforeOverlay = useCallback(() => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      lastFocusBeforeOverlayRef.current = activeElement;
    }
  }, []);

  const restoreFocusAfterOverlay = useCallback((fallback?: HTMLElement | null) => {
    window.requestAnimationFrame(() => {
      const previousFocus = lastFocusBeforeOverlayRef.current;
      const target = previousFocus?.isConnected ? previousFocus : fallback;
      target?.focus();
      lastFocusBeforeOverlayRef.current = null;
    });
  }, []);

  const groupingParameterName = useConfigurationStore(
    (state) => state.menuConfiguration.groupingParameterName
  );
  const groupingParameterSource = useConfigurationStore(
    (state) => state.menuConfiguration.groupingParameterSource
  );

  const commands = useExportWorkspaceCommands({
    exportState,
    selected,
    exportActionsBlocked,
    groupingParameterName,
    openExportSettings: () => setSettingsOpen(true),
    profile,
    confirmExportWithUnsavedSettings,
    notifyExportBlocked,
    recordExportResult,
    showSuccess,
    showWarning,
    showError,
    announce: setAccessibilityAnnouncement,
    rememberFocusBeforeOverlay,
    restoreFocusAfterOverlay,
    outputPathFocusFallbackRef: exportSettingsButtonRef,
  });
  const exportBusy = exportState.exporting || commands.pickExportBusy || commands.validationPreflightBusy;
  const visualCheck = usePcfVisualCheck({
    parameterName: groupingParameterName,
    parameterSource: groupingParameterSource,
    showSuccess,
    showWarning,
    showError,
    announce: setAccessibilityAnnouncement,
  });

  const tourRefs = useMemo<ExportTourRefs>(() => ({ headerRef, refreshButtonRef, systemCountRef, systemTableRef, systemTableToolsRef, systemTableRowsRef, firstSystemRowRef, rowHighlightButtonRef, visualCheckButtonRef, columnPanelRef: activeColumnPanelRef, propertySearchRef: activePropertySearchRef, manageColumnsButtonRef, exportSettingsButtonRef, exportSettingsDialogRef, settingsGroupingRef, settingsOutputPathRef, settingsValidationRef, selectionSummaryRef, exportActionsRef, outputPathRef, exportButtonRef, pickAndExportButtonRef }), []);

  const {
    tourOpen,
    tourStep,
    exportTourSteps,
    effectiveSettingsOpen,
    settingsPreviewOnly,
    handleOpenTour,
    handleCloseTour,
    handleOpenGuide,
    setTourStep,
  } = useExportTour({
    systemCount: exportState.systems.length,
    columnPanelOpen,
    setColumnPanelOpen,
    settingsOpen,
    setGuideOpen,
    refs: tourRefs,
    rememberFocusBeforeOverlay,
    restoreFocusAfterOverlay,
  });

  const useInlineTourDrawer = useDrawerPanel && tourOpen;
  const availableParameters = useExportColumnParameters({
    open: columnPanelOpen,
    useDrawer: useDrawerPanel,
    tourOpen,
    systems: exportState.systems,
    activePanelRef: activeColumnPanelRef,
    activeSearchRef: activePropertySearchRef,
    inlinePanelRef: inlineColumnPanelRef,
    drawerPanelRef: drawerColumnPanelRef,
    inlineSearchRef: inlinePropertySearchRef,
    drawerSearchRef: drawerPropertySearchRef,
  });

  useEffect(() => {
    if (error) {
      const isBackgroundRuntimeError = EXPORT_BACKGROUND_ERROR_PATTERN.test(error);
      const shouldSuppressGuidedRuntimeError = isBackgroundRuntimeError;

      if (shouldSuppressGuidedRuntimeError) {
        setAccessibilityAnnouncement(error);
        clearError();
        return;
      }

      showError(error);
      setAccessibilityAnnouncement(error);
      clearError();
    }
  }, [clearError, error, showError]);

  const handleRefreshSystems = useCallback(async () => {
    setAccessibilityAnnouncement('Refreshing piping systems from Revit.');
    await exportState.loadSystems();
    setAccessibilityAnnouncement('System refresh finished. Review the updated piping systems table.');
  }, [exportState]);

  const handleCloseColumnPanel = useCallback(() => {
    setColumnPanelOpen(false);
    restoreFocusAfterOverlay(manageColumnsButtonRef.current);
  }, [restoreFocusAfterOverlay]);

  const handleToggleColumnPanel = useCallback(() => {
    if (columnPanelOpen) {
      handleCloseColumnPanel();
      return;
    }
    rememberFocusBeforeOverlay();
    setColumnPanelOpen(true);
  }, [columnPanelOpen, handleCloseColumnPanel, rememberFocusBeforeOverlay]);

  const handleOpenSettings = useCallback(() => {
    rememberFocusBeforeOverlay();
    setSettingsOpen(true);
  }, [rememberFocusBeforeOverlay]);

  const handleCloseSettings = useCallback(() => {
    setSettingsOpen(false);
    restoreFocusAfterOverlay(exportSettingsButtonRef.current);
  }, [restoreFocusAfterOverlay]);

  const handleCloseGuide = useCallback(() => {
    setGuideOpen(false);
    restoreFocusAfterOverlay(headerRef.current);
  }, [restoreFocusAfterOverlay]);

  const handleParameterCheckedChange = useCallback((id: string, checked: boolean) => {
    columns.setColumnChecked(id, checked);
  }, [columns]);
  const handleAddExtraProperty = useCallback((property: PropertyItem) => {
    columns.addExtraColumn(property);
  }, [columns]);
  const handleRemoveExtraProperty = useCallback((id: string) => {
    columns.removeExtraColumn(id);
  }, [columns]);

  const panelProperties = useMemo<PropertyItem[]>(
    () => columns.allColumns
      .filter((column) => !column.isExtra)
      .map((column) => ({
        id: column.id,
        label: column.label,
        checked: column.checked,
        revitParameter: column.revitParameter,
        isReadOnly: column.isReadOnly,
        dataType: column.dataType,
      })),
    [columns.allColumns]
  );

  const exportSummary = useMemo(
    () => summarizePipingSystems(exportState.systems),
    [exportState.systems]
  );
  const systemByName = useMemo(
    () => new Map(exportState.systems.map((system) => [system.systemName, system])),
    [exportState.systems]
  );
  const selectedSystems = useMemo(() => Array.from(selected)
    .map((systemName) => systemByName.get(systemName))
    .filter((system): system is PipingSystem => Boolean(system)), [selected, systemByName]);
  const handleSelectSingleSystemInModel = useCallback((systems: PipingSystem[]) => {
    void commands.handleSelectSystemsInModel(systems);
  }, [commands]);

  const usingSelectedBatch = selectedSystems.length > 0;
  const activeBatchSystems = usingSelectedBatch ? selectedSystems : exportState.systems;
  const activeBatchSummary = useMemo(
    () => summarizePipingSystems(activeBatchSystems),
    [activeBatchSystems]
  );
  const quotaSummary = quotaExhausted
    ? `${formatRemainingExportElementsStatus(quotaIsDemo, quotaRemaining ?? 0)}. ${quotaExhaustedMessage}`
    : quotaLimited && quotaRemaining !== null
      ? `${formatRemainingExportElementsStatus(quotaIsDemo, quotaRemaining)}.`
      : '';
  const liveSummary = useExportLiveSummary({
    systems: exportState.systems,
    selected,
    loading: exportState.loading,
    systemsStale: exportState.systemsStale,
    exporting: exportBusy,
    outputPath: exportState.config.outputPath,
    activeBatchSummary,
    exportSummary,
    quotaSummary,
    accessibilityAnnouncement,
  });

  useEffect(() => {
    if (!registerEmbeddedActions) {
      return;
    }

    registerEmbeddedActions({
      openGuide: handleOpenGuide,
      openTour: handleOpenTour,
    });

    return () => {
      registerEmbeddedActions(null);
    };
  }, [handleOpenGuide, handleOpenTour, registerEmbeddedActions]);

  useExportAccessibilityWorkflow({
    headerRef,
    systemTableRef,
    columnPanelRef: activeColumnPanelRef,
    exportActionsRef,
    columnPanelOpen,
    settingsOpen,
    pathDialogOpen: commands.pathDialogState.open,
    guideOpen,
    tourOpen,
    onCloseColumnPanel: handleCloseColumnPanel,
    onCloseSettings: handleCloseSettings,
    onClosePathDialog: commands.handleClosePathDialog,
    onCloseGuide: handleCloseGuide,
    onCloseTour: handleCloseTour,
  });

  return (
    <Box
      data-testid="export-pcf-root"
      role="region"
      aria-label="PCF export workspace"
      aria-busy={exportState.loading || exportBusy || visualCheck.busy}
      aria-describedby={EXPORT_LIVE_SUMMARY_ID}
      tabIndex={-1}
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      <ExportWorkspaceHeader
        liveSummary={liveSummary}
        exportState={exportState}
        exportLog={exportLog}
        commands={commands}
        visualCheck={visualCheck}
        activeBatchSummary={activeBatchSummary}
        selected={selected}
        systemNameFilterValue={systemNameFilterValue}
        onSystemNameFilterChange={setSystemNameFilterValue}
        rowHighlightColors={rowHighlightColors}
        showGuideButton={showGuideButton}
        narrow={useNarrowExportLayout}
        columnPanelOpen={columnPanelOpen}
        groupingParameterName={groupingParameterName}
        groupingParameterSource={groupingParameterSource}
        quotaLimited={quotaLimited}
        quotaExhausted={quotaExhausted}
        quotaRemaining={quotaRemaining}
        quotaIsDemo={quotaIsDemo}
        quotaExhaustedMessage={quotaExhaustedMessage}
        exportBusy={exportBusy}
        exportActionsBlocked={exportActionsBlocked}
        exportBlockedMessage={exportBlockedMessage}
        headerRef={headerRef}
        refreshButtonRef={refreshButtonRef}
        systemCountRef={systemCountRef}
        rowHighlightButtonRef={rowHighlightButtonRef}
        visualCheckButtonRef={visualCheckButtonRef}
        manageColumnsButtonRef={manageColumnsButtonRef}
        exportSettingsButtonRef={exportSettingsButtonRef}
        selectionSummaryRef={selectionSummaryRef}
        exportActionsRef={exportActionsRef}
        outputPathRef={outputPathRef}
        exportButtonRef={exportButtonRef}
        pickAndExportButtonRef={pickAndExportButtonRef}
        onClearExportResult={clearExportResult}
        onOpenTour={handleOpenTour}
        onOpenGuide={handleOpenGuide}
        onRefreshSystems={handleRefreshSystems}
        onRowHighlightColorChange={handleRowHighlightColorChange}
        onRowHighlightReset={handleRowHighlightReset}
        onToggleColumnPanel={handleToggleColumnPanel}
        onOpenSettings={handleOpenSettings}
      />
      <ExportSystemsWorkspace
        systems={exportState.viewSystems}
        selected={selected}
        systemNameFilterValue={systemNameFilterValue}
        onSystemNameFilterChange={setSystemNameFilterValue}
        columns={columns}
        loading={exportState.loading}
        selectionDisabled={exportState.config.groupBy === 'Parameter'}
        columnPanelOpen={columnPanelOpen}
        useDrawer={useDrawerPanel}
        persistentDrawer={useInlineTourDrawer}
        panelProperties={panelProperties}
        availableParameters={availableParameters}
        rowHighlightColors={rowHighlightColors}
        modelSelectionBusy={commands.modelSelectionBusy}
        exportFailuresBySystemName={indexExportFailuresBySystemName(exportLog.log?.groupFailures ?? [])}
        onOpenExportLog={exportLog.openWindow}
        systemTableRef={systemTableRef}
        systemToolsRef={systemTableToolsRef}
        systemRowsRef={systemTableRowsRef}
        firstSystemRowRef={firstSystemRowRef}
        inlinePanelRef={inlineColumnPanelRef}
        drawerPanelRef={drawerColumnPanelRef}
        inlineSearchRef={inlinePropertySearchRef}
        drawerSearchRef={drawerPropertySearchRef}
        onSelectionChange={setSelected}
        onStatusClick={onStatusClick}
        onSelectInModel={handleSelectSingleSystemInModel}
        onCheckedChange={handleParameterCheckedChange}
        onAdd={handleAddExtraProperty}
        onRemove={handleRemoveExtraProperty}
        onClosePanel={handleCloseColumnPanel}
      />

      <ExportWorkspaceOverlays
        exportState={exportState}
        exportLog={exportLog}
        commands={commands}
        exportBusy={exportBusy}
        selectedCount={selected.size}
        effectiveSettingsOpen={effectiveSettingsOpen}
        settingsPreviewOnly={settingsPreviewOnly}
        guideOpen={guideOpen}
        tourOpen={tourOpen}
        tourStep={tourStep}
        tourSteps={exportTourSteps}
        unsavedSettingsDialog={unsavedSettingsGuardDialog}
        exportSettingsDialogRef={exportSettingsDialogRef}
        settingsGroupingRef={settingsGroupingRef}
        settingsOutputPathRef={settingsOutputPathRef}
        settingsValidationRef={settingsValidationRef}
        onCloseSettings={handleCloseSettings}
        onCloseGuide={handleCloseGuide}
        onOpenTour={handleOpenTour}
        onCloseTour={handleCloseTour}
        onSetTourStep={setTourStep}
      />

    </Box>
  );
}
